<template>
  <div class="flex flex-col gap-4">
    <div class="rounded-lg bg-white/5 border border-white/10 p-4">
      <p class="text-sm text-gray-300">
        {{ t('modal_feedback.info_what_happens') }}
      </p>
      <p class="mt-2 text-sm text-gray-400">
        {{ t('modal_feedback.info_visibility') }}
      </p>
    </div>

    <div>
      <div class="mb-1 font-medium text-white">{{ t('modal_feedback.to_client') }}</div>
      <div class="mb-4 text-sm text-gray-400">{{ t('modal_feedback.email_description') }}</div>

      <div class="space-y-3">
        <div
            v-for="(_, index) in clientEmails"
            :key="index"
            class="flex gap-2 items-center"
        >
          <BTextField
            v-model="clientEmails[index]"
            type="email"
            :placeholder="t('modal_feedback.email_placeholder')"
            class="flex-1"
            :rules="isNotCurrentUser"
          />
          <button
            v-if="clientEmails.length > 1"
            @click="removeEmail(index, 'client')"
            class="text-red-500 hover:text-red-700 px-2"
            type="button"
          >
            ✕
          </button>
        </div>
      </div>

      <button
        @click="addEmail('client')"
        class="mt-3 text-sm text-blue-500 hover:text-blue-800"
        type="button"
      >
        + {{ t('modal_feedback.add_email') }}
      </button>
    </div>

    <div v-if="isManagerType">
      <div class="mb-1 font-medium text-white">{{ t('modal_feedback.to_manager') }}</div>
      <div class="mb-4 text-sm text-gray-400">{{ t('modal_feedback.email_description') }}</div>

      <label class="flex items-center gap-2 mb-4 cursor-pointer">
        <input
          v-model="managerLeftCompany"
          type="checkbox"
          class="rounded border-gray-600 bg-white/10 text-blue-500 focus:ring-blue-500"
        />
        <span class="text-sm text-gray-300">{{ t('modal_feedback.manager_left_company') }}</span>
      </label>

      <template v-if="!managerLeftCompany">
        <div class="space-y-3">
          <div
              v-for="(_, index) in managerEmails"
              :key="index"
              class="flex gap-2 items-center"
          >
            <BTextField
                v-model="managerEmails[index]"
                type="email"
                :placeholder="t('modal_feedback.email_placeholder')"
                class="flex-1"
                :rules="isNotCurrentUser"
            />
            <button
                v-if="managerEmails.length > 1"
                @click="removeEmail(index, 'manager')"
                class="text-red-500 hover:text-red-700 px-2"
                type="button"
            >
              ✕
            </button>
          </div>
        </div>

        <button
            @click="addEmail('manager')"
            class="mt-3 text-sm text-blue-500 hover:text-blue-800"
            type="button"
        >
          + {{ t('modal_feedback.add_email') }}
        </button>
      </template>

      <p v-else class="text-sm text-gray-400 italic">
        {{ t('modal_feedback.manager_left_hint') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRefs } from 'vue';
import BTextField from "~/components/forms/BTextField.vue";
import type { Experience } from '~/api/v2/experiences/ExperienceRepository';

const { t } = useI18n();

interface Props {
  experience: Partial<Experience> | null;
  currentUserEmail: string;
  initialData?: Record<string, any>;
}

const props = defineProps<Props>();
const { initialData } = toRefs(props);
const emit = defineEmits(['change']);

const clientEmails = ref<string[]>(['']);
const managerEmails = ref<string[]>(['']);
const managerLeftCompany = ref(false);

// Flag to prevent emitting during draft restoration
const isRestoringData = ref(false)

const isManagerType = computed(() => {
  if (!props.experience || !props.experience.contract_type) return false;
  return ['permanent', 'fixed_term', 'internship', 'work_study'].includes(props.experience.contract_type);
});

const isNotCurrentUser = (value: string) => {
  if (!value) return true; // Allow empty fields
  if (value === props.currentUserEmail) {
    return t('modal_feedback.error_own_email');
  }
  return true;
};

const addEmail = (type: 'client' | 'manager') => {
  if (type === 'client') {
    clientEmails.value.push('');
  } else {
    managerEmails.value.push('');
  }
};

const removeEmail = (index: number, type: 'client' | 'manager') => {
  if (type === 'client') {
    clientEmails.value.splice(index, 1);
  } else {
    managerEmails.value.splice(index, 1);
  }
};

const isStepValid = computed(() => {
    const effectiveManagerEmails = managerLeftCompany.value ? [] : managerEmails.value;
    const allEmails = [...clientEmails.value, ...(isManagerType.value ? effectiveManagerEmails : [])];
    return allEmails.every(email => !email || email !== props.currentUserEmail);
});

watch([clientEmails, managerEmails, isStepValid, managerLeftCompany], () => {
  if (isRestoringData.value) return

  emit('change', {
    clientEmails: clientEmails.value,
    managerEmails: managerLeftCompany.value ? [] : managerEmails.value,
    managerLeftCompany: managerLeftCompany.value,
    isValid: isStepValid.value
  });
}, { deep: true });

// Watch for draft restoration
watch(initialData, (data) => {
  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true
    if (data.clientEmails && data.clientEmails.length > 0) {
      clientEmails.value = [...data.clientEmails]
    }
    if (data.managerEmails && data.managerEmails.length > 0) {
      managerEmails.value = [...data.managerEmails]
    }
    if (data.managerLeftCompany !== undefined) {
      managerLeftCompany.value = data.managerLeftCompany
    }
    // Reset flag after Vue processes all updates
    nextTick(() => {
      isRestoringData.value = false
    })
  }
}, { immediate: true, deep: true });
</script>
